import { z } from 'zod';
/**
 * Gift certificate ledger (Hub-side until Altegio takes it over; no CRM writes).
 *
 * A certificate is a stored value: `valueMinor` is what it was sold or registered for,
 * `balanceMinor` what is left. Every use is a redemption against a visit. Amounts are
 * integer minor units (cents) in the location currency.
 */
/** Certificate number as printed / encoded in the barcode. */
export const CERTIFICATE_CODE_RE = /^[A-Za-z0-9-]{3,40}$/;
/**
 * Codes are compared case-insensitively: stored and looked up in upper case. Spaces are dropped,
 * so a printed number read as "060 482 1377" is the same as "0604821377".
 */
export function normalizeCertificateCode(raw) {
    return raw.replace(/\s+/g, '').toUpperCase();
}
export const GiftCertificateCodeSchema = z
    .string()
    .transform((s) => s.replace(/\s+/g, ''))
    .pipe(z.string().regex(CERTIFICATE_CODE_RE, 'code'))
    .transform(normalizeCertificateCode);
// ───────────── printed certificates (owner 15.10.2026) ─────────────
//
// Numbers are printed on the certificates in advance and are the same code in Altegio
// (Altegio lets staff type the gift card code at sale). 10 digits, digits only so they are easy
// to type in Altegio and on the tablet:   VVV SSSSSS C
//   VVV    value in euros (040 … 150) — the value can be read from the number itself;
//   SSSSSS random serial;
//   C      Luhn check digit — a mistyped digit or two swapped neighbours is refused.
export const PRINTED_CODE_RE = /^\d{10}$/;
/** Luhn check digit for a string of digits. */
export function luhnCheckDigit(digits) {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        let d = Number(digits[digits.length - 1 - i]);
        if (i % 2 === 0) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
    }
    return (10 - (sum % 10)) % 10;
}
/** Builds a printed number from a whole-euro value (≤ 999 €) and a serial 0 … 999 999. */
export function printedCertificateCode(valueMinor, serial) {
    if (!Number.isSafeInteger(valueMinor) || valueMinor <= 0 || valueMinor % 100 !== 0 || valueMinor > 99_900) {
        throw new RangeError('valueMinor must be whole euros up to 999');
    }
    if (!Number.isSafeInteger(serial) || serial < 0 || serial > 999_999)
        throw new RangeError('serial');
    const body = `${String(valueMinor / 100).padStart(3, '0')}${String(serial).padStart(6, '0')}`;
    return `${body}${luhnCheckDigit(body)}`;
}
/** The code looks like a printed number (10 digits); says nothing about the check digit. */
export function isPrintedFormat(code) {
    return PRINTED_CODE_RE.test(code);
}
/** 10 digits with a correct check digit. */
export function isValidPrintedCode(code) {
    return isPrintedFormat(code) && luhnCheckDigit(code.slice(0, 9)) === Number(code[9]);
}
/** Value printed in the number, in cents. */
export function printedCodeValueMinor(code) {
    return Number(code.slice(0, 3)) * 100;
}
/** "0604821377" → "060 482 1377" (as printed; spaces are ignored when typed back). */
export function formatCertificateCode(code) {
    return isPrintedFormat(code) ? `${code.slice(0, 3)} ${code.slice(3, 6)} ${code.slice(6)}` : code;
}
/** Last valid day of a certificate sold on `soldOn` (YYYY-MM-DD): same day `months` later, minus one. */
export function certificateExpiresOn(soldOn, months) {
    const [y, m, d] = soldOn.split('-').map(Number);
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, lastDay));
    target.setUTCDate(target.getUTCDate() - 1);
    return target.toISOString().slice(0, 10);
}
export function toGiftCertificateView(c) {
    return {
        code: c.code,
        valueMinor: c.valueMinor,
        balanceMinor: c.balanceMinor,
        currency: c.currency,
        state: c.state ?? 'active',
        ...(c.expiresOn ? { expiresOn: c.expiresOn } : {}),
    };
}
/**
 * How a bill is split when a certificate is used: the certificate covers
 * min(balance, bill); the rest of the bill is paid otherwise, the rest of the balance stays.
 */
export function splitWithCertificate(billMinor, balanceMinor) {
    for (const [name, v] of [
        ['billMinor', billMinor],
        ['balanceMinor', balanceMinor],
    ]) {
        if (!Number.isSafeInteger(v) || v < 0)
            throw new RangeError(`${name} must be a non-negative integer`);
    }
    const certificateMinor = Math.min(billMinor, balanceMinor);
    return {
        certificateMinor,
        remainderMinor: billMinor - certificateMinor,
        balanceAfterMinor: balanceMinor - certificateMinor,
    };
}
/** Highest value staff may register for an older certificate (sanity limit, cents). */
export const MAX_REGISTERED_CERTIFICATE_MINOR = 100_000;
export const RegisterGiftCertificateRequestSchema = z.object({
    code: GiftCertificateCodeSchema,
    valueMinor: z.number().int().min(100).max(MAX_REGISTERED_CERTIFICATE_MINOR),
    pin: z.string().regex(/^\d{4,8}$/),
});
// ───────────── print batches and Altegio bookkeeping ─────────────
/** Most certificates of one value in one print batch. */
export const PRINT_BATCH_MAX_PER_VALUE = 50;
export const CreatePrintBatchRequestSchema = z.object({
    /** Values to print (each must be one of the location's certificate values). */
    valuesMinor: z.array(z.number().int().positive()).min(1).max(10),
    count: z.number().int().min(1).max(PRINT_BATCH_MAX_PER_VALUE),
    pin: z.string().regex(/^\d{4,8}$/),
});
export const MarkAltegioRequestSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('sale') }),
    z.object({ kind: z.literal('redemption'), visitId: z.string().min(1).max(200) }),
]);
//# sourceMappingURL=gift-certificate.js.map